import { useState, useCallback, useEffect } from 'react';
import { useAIChatPinStore } from '../../../stores/aiChatPinStore';

// Extend Window interface to include Document Picture-in-Picture API
declare global {
  interface Window {
    documentPictureInPicture?: {
      requestWindow(options?: { width?: number; height?: number }): Promise<Window>;
    };
  }
}

export function usePopOutWindow() {
  const [isPoppedOut, setIsPoppedOutLocal] = useState(false);
  const { setPoppedOut: setGlobalPoppedOut } = useAIChatPinStore();

  const setIsPoppedOut = useCallback((value: boolean) => {
    setIsPoppedOutLocal(value);
    setGlobalPoppedOut(value);
  }, [setGlobalPoppedOut]);

  const [popOutWindow, setPopOutWindow] = useState<Window | null>(null);

  const copyStyles = (targetDocument: Document) => {
    // 0. Remove any previously copied styles to prevent duplicate rules stacking up on reuse
    targetDocument.head.querySelectorAll('style[data-pip="true"], link[data-pip="true"]').forEach(el => el.remove());

    // 1. Iterate over all stylesheets in the main document
    Array.from(document.styleSheets).forEach((sheet) => {
      try {
        // Try to access cssRules. If it succeeds, it's a local or dynamic stylesheet.
        if (sheet.cssRules) {
          const newStyle = targetDocument.createElement('style');
          newStyle.setAttribute('data-pip', 'true');
          Array.from(sheet.cssRules).forEach((rule) => {
            newStyle.appendChild(targetDocument.createTextNode(rule.cssText));
          });
          targetDocument.head.appendChild(newStyle);
        }
      } catch (err) {
        // If accessing cssRules throws a CORS error, it's an external stylesheet (like Google Fonts).
        // We clone the original <link> tag instead.
        if (sheet.ownerNode) {
          const clone = sheet.ownerNode.cloneNode(true) as HTMLElement;
          clone.setAttribute('data-pip', 'true');
          targetDocument.head.appendChild(clone);
        }
      }
    });
  };

  const closePopOut = useCallback(() => {
    if (popOutWindow) {
      popOutWindow.close();
      setPopOutWindow(null);
    }
    setIsPoppedOut(false);
  }, [popOutWindow]);

  const openPopOut = useCallback(async () => {
    if (isPoppedOut) {
      closePopOut();
      return;
    }

    try {
      let newWindow: Window | null = null;

      // Check if Document PiP API is supported
      if ('documentPictureInPicture' in window && window.documentPictureInPicture) {
        newWindow = await window.documentPictureInPicture.requestWindow({
          width: 400,
          height: 600,
        });
      } else {
        // Fallback to standard window.open
        newWindow = window.open(
          '',
          'AIChatAssistant',
          'width=400,height=600,menubar=no,toolbar=no,location=no,status=no'
        );
      }

      if (newWindow) {
        copyStyles(newWindow.document);
        
        // Handle when the user closes the window natively
        newWindow.addEventListener('pagehide', () => {
          setIsPoppedOut(false);
          setPopOutWindow(null);
        });

        setPopOutWindow(newWindow);
        setIsPoppedOut(true);
      }
    } catch (err) {
      console.error('Failed to open pop-out window:', err);
    }
  }, [isPoppedOut, closePopOut]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (popOutWindow) {
        popOutWindow.close();
      }
    };
  }, [popOutWindow]);

  return {
    isPoppedOut,
    popOutWindow,
    togglePopOut: openPopOut,
    closePopOut,
  };
}
